import { useMemo } from "react";
import * as Bananas from "bananas-commerce-admin";

import { Case, Reception } from "@/extensions/klarahill/types";

export interface InformationCardProps extends Bananas.CardProps {
  case: Case;
  reception: Reception | undefined;
  setReception: React.Dispatch<React.SetStateAction<Reception | undefined>>;
}

export interface InformationCardFormValues {
  visibility: "on" | "off";
  information: string;
  place: string;
  max_attendees: string;
  date: string;
  rsvp_date: string;
}

export const InformationCard: React.FC<InformationCardProps> = ({
  case: c,
  reception,
  setReception,
  ...props
}) => {
  const api = Bananas.useApi();

  const attendees = useMemo(
    () =>
      c.memorial_service_attendees.reduce((acc, attendee) => {
        if (attendee.reserve) return acc;

        return acc + attendee.count;
      }, 0),
    [c],
  );

  return reception == null ? null : (
    <Bananas.Card<InformationCardFormValues>
      isCompact
      isEditable
      onSubmit={async (values) => {
        const action = api.operations["case.case:update-reception"];
        if (!action)
          throw new Error('Invalid action "case.case:update-reception".');

        const visibility = values["visibility"] === "on";
        const information = values["information"];
        const rsvp_date = new Date(values["rsvp_date"]).toLocaleDateString(
          "sv-SE",
          { year: "numeric", month: "2-digit", day: "2-digit" },
        );

        const max_attendees =
          values["max_attendees"] === "" ? null : values["max_attendees"];

        const response = await action.call({
          params: { case_id: c.id },
          body: {
            visibility,
            information,
            rsvp_date,
            max_attendees,
          },
        });

        if (response.ok) {
          const updatedReception: Reception = await response.json();
          setReception(updatedReception);
        } else {
          console.error(response);
          throw new Error("uppdatering av minnesstund");
        }

        return "Inställningar uppdaterade.";
      }}
      {...props}
    >
      <Bananas.CardHeader title="Information" />

      <Bananas.CardContent>
        <Bananas.CardRow>
          <Bananas.CardFieldSwitch
            formName="visibility"
            helperText="Visa minnesstunden på minnessidan."
            label="Synlighet"
            value={reception?.visibility ?? false}
          />
        </Bananas.CardRow>

        <Bananas.CardRow>
          <Bananas.CardFieldText
            multiline
            formName="information"
            helperText='Fritextfält för information gällande minnesstunden som visas på minnessidan. Exempelvis: "Efter akten inbjudes till minnesstund, anmäl senast 12 feb."'
            label="Information"
            minRows={2}
            value={reception?.information ?? ""}
          />
        </Bananas.CardRow>

        <Bananas.CardRow>
          <Bananas.CardFieldText
            disabled
            formName="place"
            label="Plats"
            value={reception?.location_name}
          />
        </Bananas.CardRow>

        <Bananas.CardRow>
          <Bananas.CardFieldNumber
            disabled
            formName="attendees"
            label="Antal gäster"
            value={attendees}
          />
        </Bananas.CardRow>

        <Bananas.CardRow>
          <Bananas.CardFieldNumber
            fallback="Obegränsat"
            fallbackPredicate={(max) => max == null || max === 0}
            formName="max_attendees"
            helperText="Max antal gäster som kan anmäla sig till minnesstunden. Lämna tomt för obegränsat antal gäster."
            label="Max antal gäster"
            min={0}
            value={reception?.max_attendees}
          />
        </Bananas.CardRow>

        <Bananas.CardRow>
          <Bananas.CardFieldDate
            disabled
            formName="date"
            label="När"
            value={c?.funeral?.funeral_at}
          />
        </Bananas.CardRow>

        <Bananas.CardRow>
          <Bananas.CardFieldDate
            formName="rsvp_date"
            label="Sista anm."
            value={reception?.rsvp_date}
          />
        </Bananas.CardRow>
      </Bananas.CardContent>

      <Bananas.CardActions>
        <Bananas.CardCancelButton />
        <Bananas.CardSaveButton />
      </Bananas.CardActions>
    </Bananas.Card>
  );
};
